import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { AlertTriangle } from 'lucide-react';
import { allowedEvents, isDestructiveEvent, isOverdue } from '../../Helpers/keyRegisterUtils';
import { tenantPath } from '../../types/tenants';
import KeyHistory from './KeyHistory';
import {
    KeyFieldsInput,
    KeyHolder,
    KeyType,
    keyStatusLabel,
    KEY_EVENT_ACTION_LABELS,
    keyHolderKindLabel,
    RecordableKeyEventType,
    RegisterKey,
} from '../../types/keyRegister';

interface KeyDetailProps {
    registerKey: RegisterKey;
    type: KeyType;
    today: string;
    submitting: boolean;
    holderName: (holder: KeyHolder | undefined) => string;
    actorName: (uid: string) => string;
    onSaveFields: (input: KeyFieldsInput) => Promise<void>;
    onStartEvent: (eventType: RecordableKeyEventType) => void;
    getReceiptUrl: (storagePath: string) => Promise<string>;
}

interface FieldValues {
    serial: string;
    location: string;
}

const fieldsSchema = Yup.object({
    serial: Yup.string().trim().max(50, 'Max 50 tecken'),
    location: Yup.string().trim().max(200, 'Max 200 tecken'),
});

/** The body of the key modal: status and the Hantera menu, holder, fields and log. */
const KeyDetail: React.FC<KeyDetailProps> = ({
    registerKey, type, today, submitting, holderName, actorName, onSaveFields, onStartEvent, getReceiptUrl,
}) => {
    const tenantTerm = useTenantTerm();
    const overdue = isOverdue(registerKey, today);
    const actions = allowedEvents(registerKey, type.kind);
    const statusActions = actions.filter(action => action !== 'comment');

    const saveFields = async (values: FieldValues, helpers: FormikHelpers<FieldValues>) => {
        helpers.setStatus(undefined);
        try {
            await onSaveFields({ serial: values.serial.trim() || undefined, location: values.location.trim() || undefined });
            helpers.resetForm({ values });
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte spara nyckeln');
        }
    };

    const holderLink = registerKey.holder?.kind === 'tenant' ? tenantPath(registerKey.holder.key) : null;

    return (
        <div className="key-detail">
            <div className="key-detail-status">
                <span className={`key-pill key-pill--${registerKey.status}`}>
                    {keyStatusLabel(registerKey)}
                </span>
                {overdue && <span className="key-detail-overdue-tag">Försenad</span>}
                {actions.length > 0 && (
                    <Dropdown align="end" className="key-detail-actions">
                        <Dropdown.Toggle variant="outline-primary" className="key-detail-actions-toggle" disabled={submitting}>
                            Hantera
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="key-detail-actions-menu" data-testid="key-actions-menu">
                            {actions.includes('comment') && (
                                <Dropdown.Item as="button" type="button" onClick={() => onStartEvent('comment')}>
                                    {KEY_EVENT_ACTION_LABELS.comment}
                                </Dropdown.Item>
                            )}
                            {actions.includes('comment') && statusActions.length > 0 && <Dropdown.Divider />}
                            {statusActions.map(action => (
                                <Dropdown.Item
                                    key={action}
                                    as="button"
                                    type="button"
                                    className={isDestructiveEvent(action) ? 'text-danger' : undefined}
                                    onClick={() => onStartEvent(action)}
                                >
                                    {KEY_EVENT_ACTION_LABELS[action]}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>

            {overdue && (
                <Alert variant="warning" className="key-detail-alert">
                    <AlertTriangle size={16} className="me-2" aria-hidden="true" />
                    Skulle ha återlämnats senast {registerKey.dueDate}.
                </Alert>
            )}

            {registerKey.status === 'handedOut' && (
                <dl className="key-detail-facts">
                    <div>
                        <dt>Innehavare</dt>
                        <dd>
                            {holderLink ? <Link to={holderLink}>{holderName(registerKey.holder)}</Link> : holderName(registerKey.holder)}
                            {registerKey.holder && <span className="text-muted"> · {keyHolderKindLabel(registerKey.holder.kind, tenantTerm)}</span>}
                        </dd>
                    </div>
                    <div>
                        <dt>Återlämnas senast</dt>
                        <dd>{registerKey.dueDate ?? 'Inget datum'}</dd>
                    </div>
                </dl>
            )}

            <Formik<FieldValues>
                initialValues={{ serial: registerKey.serial ?? '', location: registerKey.location ?? '' }}
                validationSchema={fieldsSchema}
                enableReinitialize
                onSubmit={saveFields}
            >
                {({ values, errors, status, dirty, handleChange, handleBlur, handleSubmit, setStatus, resetForm }) => (
                    <Form noValidate onSubmit={handleSubmit} className="key-detail-fields">
                        {status && <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>}
                        <div className="key-detail-fields-grid">
                            <Form.Group controlId="key-serial">
                                <Form.Label>Nummer</Form.Label>
                                <Form.Control name="serial" value={values.serial} onChange={handleChange} onBlur={handleBlur} isInvalid={!!errors.serial} />
                                <Form.Control.Feedback type="invalid">{errors.serial}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId="key-location">
                                <Form.Label>Förvaringsplats</Form.Label>
                                <Form.Control name="location" value={values.location} onChange={handleChange} onBlur={handleBlur}
                                    isInvalid={!!errors.location} placeholder="T.ex. Nyckelskåp A, krok 12" />
                                <Form.Control.Feedback type="invalid">{errors.location}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        {dirty && (
                            <div className="key-detail-fields-actions">
                                <Button variant="secondary" size="sm" onClick={() => resetForm()} disabled={submitting}>Ångra</Button>
                                <Button variant="primary" size="sm" type="submit" disabled={submitting}>
                                    {submitting && <Spinner animation="border" size="sm" className="me-2" />}Spara
                                </Button>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>

            <KeyHistory log={registerKey.log} holderName={holderName} actorName={actorName} getReceiptUrl={getReceiptUrl} />
        </div>
    );
};

export default KeyDetail;
